import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import type { EditorPointer, EditorPointerAPI } from '@wix/editorx-core-api';
import { ManifestServices } from '@wix/editor-platform-manifest-services';
import {
  createBuildPathsFn,
  createPanelsLoader,
  getEditorPointersTreeAsArray,
} from './manifestStructureUtils';
import { PanelsStore } from '../../services/builderServices/panelsStore';
import {
  getComponentsOnCurrentPage,
  getCompTypeFromEditorPointer,
  getEditorPointersFromCompTypes,
  handleErrors,
} from './helpers';

type BaseProcessManifestsProperties = {
  documentServicesAPI: DocumentServicesObject;
  editorPointerAPI: EditorPointerAPI;
  manifestServices: ManifestServices;
  panelsStore: PanelsStore;
  customBuildPaths?: ReturnType<typeof createBuildPathsFn>;
  componentTypes?: string[];
};

type ProcessManifests = BaseProcessManifestsProperties & {
  loadAndResolveMethod:
    | ManifestServices['loadAndResolveManifestsByCompTypes']
    | ManifestServices['forceLoadAndResolveManifestsByCompTypes'];
};

type ProcessManifestsByCompTypes = Omit<
  BaseProcessManifestsProperties,
  'componentTypes'
> & {
  compTypes: string[];
};

function processManifests({
  documentServicesAPI,
  editorPointerAPI,
  manifestServices,
  panelsStore,
  customBuildPaths,
  componentTypes,
  loadAndResolveMethod,
}: ProcessManifests): void {
  const getManifest = (editorPointer: EditorPointer<CompRef>) =>
    manifestServices.getElementManifest({
      compType: getCompTypeFromEditorPointer(
        editorPointer,
        editorPointerAPI,
        documentServicesAPI,
      ),
      innerElementPath: editorPointerAPI.getInnerElementPath(editorPointer),
    });

  const buildPaths = createBuildPathsFn(
    documentServicesAPI,
    manifestServices.getManifestsByCompTypes,
  );
  const panelsLoader = createPanelsLoader(panelsStore, getManifest);

  const compTypes =
    componentTypes ?? getComponentsOnCurrentPage(documentServicesAPI);
  const pageEditorPointers = getEditorPointersFromCompTypes(
    documentServicesAPI,
    editorPointerAPI,
    compTypes,
  );

  loadAndResolveMethod(compTypes)
    .then(customBuildPaths ?? buildPaths)
    .then(async (resolvedManifests) => {
      await panelsLoader(
        getEditorPointersTreeAsArray(
          editorPointerAPI,
          pageEditorPointers,
          getManifest,
        ),
      );
      return resolvedManifests;
    })
    .then((resolvedManifests) => {
      documentServicesAPI.components.innerElements.setManifests(
        resolvedManifests as any,
      );
    })
    .catch(handleErrors);
}

export function loadAndProcessManifests({
  documentServicesAPI,
  editorPointerAPI,
  manifestServices,
  panelsStore,
  customBuildPaths,
  componentTypes,
}: BaseProcessManifestsProperties): void {
  processManifests({
    documentServicesAPI,
    editorPointerAPI,
    manifestServices,
    panelsStore,
    customBuildPaths,
    componentTypes,
    loadAndResolveMethod: (compTypes: string[]) =>
      manifestServices.loadAndResolveManifestsByCompTypes(compTypes),
  });
}

export function loadAndProcessManifestsByCompTypes({
  documentServicesAPI,
  editorPointerAPI,
  manifestServices,
  panelsStore,
  compTypes,
  customBuildPaths,
}: ProcessManifestsByCompTypes): void {
  loadAndProcessManifests({
    documentServicesAPI,
    editorPointerAPI,
    manifestServices,
    panelsStore,
    customBuildPaths,
    componentTypes: compTypes,
  });
}

export function forceLoadAndProcessManifestsByCompTypes({
  documentServicesAPI,
  editorPointerAPI,
  manifestServices,
  panelsStore,
  compTypes,
  customBuildPaths,
}: ProcessManifestsByCompTypes): void {
  processManifests({
    documentServicesAPI,
    editorPointerAPI,
    manifestServices,
    panelsStore,
    customBuildPaths,
    componentTypes: compTypes,
    loadAndResolveMethod: (compTypes: string[]) =>
      manifestServices.forceLoadAndResolveManifestsByCompTypes(compTypes),
  });
}
